<script setup lang="ts">
import { computed, inject, ref, unref } from "vue";
import { storeToRefs } from "pinia";
import { type RouteParamValue, useRoute } from "vue-router";

import { useAuthStore } from "@/stores/Auth";
import { usePagingFilter } from "@/composables/filters/paging-filter";

import type { SearchContextWrapper } from "@tmi/types/context/search-context";
import { searchContextSymbol } from "@/constants";

import DynamicAd from "@/components/ads/DynamicAd.vue";
import ContentPreview from "@/components/content/ContentPreview.vue";
import CategoriesFilter from "@/components/filters/Categories.vue";
import DateRangeFilter from "@/components/filters/DateRange.vue";
import FreeNewsletterSignup from "@/components/signups/FreeNewsletterSignup.vue";
import PageLayout from '@/components/layout/PageLayout.vue';
import ProductsFilter from "@/components/filters/Products.vue";
import RecentlyViewedContent from "@/components/content/RecentlyViewedContent.vue";
import TextSearchFilter from "@/components/filters/TextSearch.vue";

const { isAuthenticated } = storeToRefs( useAuthStore() );

// Provide the search manager to all subcomponents
const injectedContext = inject< SearchContextWrapper | undefined >( searchContextSymbol );
if ( !injectedContext ) {
  throw new Error( `The expected provided value for 'searchContextSymbol' does not exist in this context.` );
}

const {
  breadcrumbItems,
  contentCategories,
  contentTitle,
  contentType,
  highlightedContent,
  filteredContent,
  filteredContentPage,
  hasAnyResults,
  hideCategoryFilter,
  hideProductFilter,
  isLoading,
  searchPlaceholder,
  searchResultsLabel,
  showTaggedIn,
  hideDateFilter
} = injectedContext!;

const showLoading = computed( () => ( isLoading ? unref( isLoading ) : false ) );

const { pageNumber, pageSize } = usePagingFilter();

const route = useRoute();
const slug = computed< string | RouteParamValue[] | undefined >( () => route?.params?.slug );
const hasSlug = computed< boolean >( () => !!( slug.value ) );

const showCategoriesFilter = computed< boolean >( () => isAuthenticated.value && !!contentCategories && contentCategories.value.length > 1 && !hideCategoryFilter );
const showPagination = computed< boolean >( () => filteredContent.value.length > pageSize.value );
const totalRecords = computed< number >( () => filteredContent.value.length );
const resultsLabelText = computed< string >( () => {
  const label = searchResultsLabel ? unref( searchResultsLabel ) : '';
  return ( label ?? '' ).trim();
} );
const showProductFilter = ref< boolean >( isAuthenticated.value && !!( contentTitle ) && !hideProductFilter );
const showTaggedInLabel = ref< boolean >( !!( showTaggedIn ) && hasSlug.value );
const showDateFilter = ref< boolean >( !hideDateFilter );
</script>

<template>
  <PageLayout
    :title="breadcrumbItems ? undefined : contentTitle"
    :breadcrumb-items="breadcrumbItems"
    :compact-header="Boolean( breadcrumbItems )"
    :content-card="false"
  >
    <ProgressSpinner v-if="showLoading" />
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-y-4 lg:gap-8">
      <main class="col-span-2 order-last lg:order-first">
        <div
          v-if="!hasAnyResults"
          class="rounded-2xl border border-dashed border-base-300/80 bg-base-200/30 p-10 text-center text-base-content/60"
        >
          <i class="pi pi-search mb-3 text-2xl text-base-content/40"></i>
          <p class="font-medium text-base-content/70">No results found</p>
          <p class="mt-1 text-sm">Try adjusting your filters or search text.</p>
        </div>

        <div class="flex flex-col gap-3">
          <ContentPreview :slug="content.slug" :content-type="contentType ?? ''" v-for="( content ) in highlightedContent" :key="content.slug" :highlight="true" />
          <ContentPreview :slug="content.slug" :content-type="contentType ?? ''" v-for="( content ) in filteredContentPage" :key="content.slug" />
        </div>
        <div class="mt-6 flex justify-center">
          <Paginator
            v-if="showPagination"
            :first="pageNumber"
            :totalRecords="totalRecords"
            :rows="pageSize"
            :alwaysShow="false"
            class="content-search-paginator"
            @update:first="( value ) => pageNumber = value"
          />
        </div>
        <div v-if="resultsLabelText" class="mt-3 text-center text-xs text-base-content/60">
          {{ resultsLabelText }}
        </div>
        <div class="mt-4">
          <DynamicAd v-if="hasAnyResults" ad-type="banner" site-location="article" />
        </div>
      </main>

      <aside class="w-full space-y-5">
        <div class="rounded-2xl bg-linear-to-b from-base-100 to-base-100/90 p-4 shadow-sm">
          <div class="mb-3 flex items-center gap-2 border-b border-base-300/60 pb-2">
            <span class="inline-flex size-7 items-center justify-center rounded-md bg-primary/10 text-primary">
              <i class="pi pi-sliders-h text-xs"></i>
            </span>
            <h6 class="text-sm font-semibold uppercase tracking-wide text-base-content/70">Filters</h6>
          </div>
          <div class="space-y-3">
            <TextSearchFilter :placeholder="searchPlaceholder" />
            <DateRangeFilter v-if="showDateFilter"/>
            <ProductsFilter v-if="showProductFilter" />
            <CategoriesFilter v-if="showCategoriesFilter" :content-categories="contentCategories" />
          </div>
        </div>
        <FreeNewsletterSignup v-if="!isAuthenticated" />
        <RecentlyViewedContent v-if="isAuthenticated" />
        <DynamicAd ad-type="side-right" site-location="article" />
        <DynamicAd ad-type="popup" site-location="article" />

        <div v-if="showTaggedInLabel" class="text-base">
          Tagged in:<br>
          {{ `"${ slug }"` }}
        </div>
      </aside>

    </div>
  </PageLayout>
</template>

<style scoped>
:deep(.content-search-paginator) {
  width: auto;
  border: none !important;
  border-radius: 0;
  background: transparent !important;
  padding: 0;
  box-shadow: none;
}

:deep(.content-search-paginator .p-paginator-content) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}

:deep(.content-search-paginator .p-paginator-page),
:deep(.content-search-paginator .p-paginator-first),
:deep(.content-search-paginator .p-paginator-prev),
:deep(.content-search-paginator .p-paginator-next),
:deep(.content-search-paginator .p-paginator-last) {
  min-width: 2.2rem;
  height: 2.2rem;
  background: var(--color-base-100);
  border-radius: 9999px;
  border: 1px solid color-mix(in oklch, var(--color-base-300) 75%, transparent);
  color: color-mix(in oklch, var(--color-base-content) 70%, transparent);
  transition: all 160ms ease;
  box-shadow: 0 2px 8px -6px color-mix(in oklch, var(--color-base-content) 45%, transparent);
}

:deep(.content-search-paginator .p-paginator-page:hover),
:deep(.content-search-paginator .p-paginator-first:hover),
:deep(.content-search-paginator .p-paginator-prev:hover),
:deep(.content-search-paginator .p-paginator-next:hover),
:deep(.content-search-paginator .p-paginator-last:hover) {
  border-color: color-mix(in oklch, var(--color-base-content) 25%, var(--color-base-300));
  background: var(--color-base-200);
  color: var(--color-base-content);
  transform: translateY(-1px);
}

:deep(.content-search-paginator .p-paginator-page.p-paginator-page-selected) {
  border-color: color-mix(in oklch, var(--color-base-content) 40%, var(--color-base-300));
  background: color-mix(in oklch, var(--color-base-content) 18%, var(--color-base-100));
  color: var(--color-base-content);
  font-weight: 700;
  box-shadow: 0 8px 16px -12px color-mix(in oklch, var(--color-base-content) 45%, transparent);
}

:deep(.content-search-paginator .p-disabled) {
  opacity: 0.35;
  box-shadow: none;
}
</style>
